function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import Grid from '.';
import { chainedDataIndexToObject } from '../../common/utils/chainedDataIndex';
import { FilterType } from '../../common/utils/data';
export const FilterConvertableType = {
  like: ".contains",
  gt: ".gt",
  gte: ".gte",
  lt: ".lt",
  lte: ".lte",
  multiple: ".in",
  multiple_exclusion: ".nin",
  dropdown: ".equals",
  bool: ".equals",
  integer: ".equals",
  decimal: ".equals",
  manual: ".equals"
};
;
//@ts-ignore
const Graph = /*#__PURE__*/forwardRef(({
  gql,
  defaultVariables,
  itemsKey,
  takeKey,
  skipKey,
  manageFilterParameters,
  manageFilterWhere,
  ...props
}, ref) => {
  const gridReference = useRef(null);
  const [{
    page,
    perPage,
    filters,
    sort
  }, setTable] = useState({
    page: props.defaultPage || 1,
    perPage: props.defaultPerPage || 10,
    filters: props.defaultFilter || [],
    sort: props.defaultSort
  });
  const mounted = useRef(false);
  const gqlLoaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const [loadGridQuery, requestGridQuery] = useLazyQuery(gql, {
    fetchPolicy: "no-cache"
  });

  const updateGrid = ({
    backToFirstPage = false,
    reload = false,
    isDelete = false
  } = {}) => {
    if (isDelete) {
      // advance delete one count so we know what is the new total, if the total page is lesser than before
      // and the current page is on the last page we set the current page to the last page
      const newTotalPages = Math.max(0, Math.ceil((response.current.totalCount - 1) / perPage));

      if (newTotalPages > 0 && newTotalPages < page) {
        gridReference.current?.setPage(newTotalPages);
      } else {
        updateGrid({
          backToFirstPage: false,
          reload: true,
          isDelete: false
        });
      }
    } else {
      if (reload && backToFirstPage && page !== 1) {
        return gridReference.current?.setPage(1);
      }

      let parameters = filters.length > 0 ? filters.filter(filter => filter.type === FilterType.PARAMETER).reduce((result, filter) => {
        result[filter.dataIndex] = filter.value;
        return result;
      }, {}) : null;

      if (!!parameters && !!manageFilterParameters) {
        parameters = manageFilterParameters(parameters);
      }

      let where = filters.length > 0 ? filters.filter(filter => filter.type !== FilterType.PARAMETER).reduce((result, filter) => {
        let value;

        switch (filter.type) {
          case FilterType.INTEGER:
          case FilterType.DECIMAL:
            value = filter.type === FilterType.INTEGER ? parseInt(filter.value) : parseFloat(filter.value);

            if (isNaN(value)) {
              return result;
            }

            break;

          case FilterType.BOOLEAN:
            value = filter.value === 'true';
            break;

          case FilterType.DATE:
            value = filter.value === 'true';
            return filter.value.reduce((rr, value, index) => {
              return chainedDataIndexToObject(`${filter.dataIndex}${FilterConvertableType[index === 0 ? 'gte' : 'lte']}`, value, rr);
            }, result);
            break;

          case FilterType.GREATER_THAN:
          case FilterType.GREATER_THAN_EQUAL:
          case FilterType.LESS_THAN:
          case FilterType.LESS_THAN_EQUAL:
            value = parseFloat(filter.value);
            break;

          default:
            value = filter.value;
            break;
        }

        return Array.isArray(filter.dataIndex) ? {
          or: filter.dataIndex.reduce((orResult, dataIndex) => {
            return chainedDataIndexToObject(`${dataIndex}${FilterConvertableType[filter.type]}`, value, orResult);
          }, result.or)
        } : chainedDataIndexToObject(`${filter.dataIndex}${FilterConvertableType[filter.type]}`, value, result);
      }, {}) : null;

      if (!!where && !!manageFilterWhere) {
        where = manageFilterWhere(where);
      }

      const order = sort?.order ? chainedDataIndexToObject(sort?.orderBy, sort?.order.toLocaleUpperCase()) : null;
      const cursor = perPage * page - perPage;
      const defaultWhere = defaultVariables?.where;
      const variables = { ...defaultVariables,
        ...parameters,
        [takeKey]: perPage,
        [skipKey]: backToFirstPage || cursor === 0 ? null : skipKey === 'after' ? btoa(`${cursor - 1
        /*minus 1 to get the right cursor*/
        }`) : cursor,
        order,
        where: { ...defaultWhere,
          ...where
        }
      };
      loadGridQuery({
        variables
      });

      if (gqlLoaded.current) {
        setLoading(true);
      }

      gqlLoaded.current = true;
    }
  };

  useLayoutEffect(() => {
    if (mounted.current) {
      updateGrid();
    } // eslint-disable-next-line

  }, [page, sort, perPage, filters]);
  useImperativeHandle(ref, () => ({ ...gridReference.current,
    reload: (backToFirstPage = false) => {
      updateGrid({
        backToFirstPage,
        reload: true,
        isDelete: false
      });
    },
    deleteReload: () => {
      updateGrid({
        backToFirstPage: false,
        reload: true,
        isDelete: true
      });
    } // eslint-disable-next-line

  }), [page, perPage, sort, filters, defaultVariables]);
  const response = useRef({
    data: requestGridQuery.data?.grid[itemsKey] || [],
    totalCount: requestGridQuery.data?.grid.totalCount || 0
  });

  if (requestGridQuery.data?.grid[itemsKey]) {
    response.current.data = requestGridQuery.data?.grid[itemsKey];
  }

  if (typeof requestGridQuery.data?.grid.totalCount === 'number') {
    response.current.totalCount = requestGridQuery.data?.grid.totalCount;
  }

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    }

    setLoading(false); // eslint-disable-next-line
  }, [response.current.data]);
  return /*#__PURE__*/React.createElement(Grid, _extends({
    ref: gridReference,
    totalCount: response.current.totalCount,
    loading: loading,
    facadeLoading: requestGridQuery.loading,
    onChange: e => setTable(e)
  }, props, {
    data: props.data || response.current.data
  }));
});
Graph.defaultProps = {
  itemsKey: 'items',
  takeKey: 'take',
  skipKey: 'skip'
};
export default Graph;